import {faDownload} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Meta} from '@rdcomponents/meta/meta';
import {PageCardDivider} from '@rdcomponents/page-card-divider/page-card-divider';
import {PageHeaderTitle} from '@rdcomponents/page-header-title/page-header-title';
import {Button} from 'antd';
import dayjs from 'dayjs';
import {useCallback, useContext, useState} from 'react';

import {appRoute} from '../../../../../app-route';
import {BreadCrumbs} from '../../../../../layout/bread-crumbs/bread-crumbs';
import {ExportEmailsModal} from '../../../../../layout/export-emails-modal/export-emails-modal';
import {Page} from '../../../../../layout/page/page';
import {MainPageContainer} from '../../../../../layout/page-card/main-page-card';
import {PageCard} from '../../../../../layout/page-card/page-card';
import {PageHeader} from '../../../../../layout/page-header/page-header';
import {Locale} from '../../../../../provider/locale/locale';
import {useLocale} from '../../../../../provider/locale/locale-hook';
import {MainFilterContext} from '../../../../../provider/main-filter/main-filter';
import {MainFilterContextType} from '../../../../../provider/main-filter/main-filter-type';
import {useSnackbar} from '../../../../../provider/snackbar/snackbar-hook';
import {useSavedTableSort} from '../../../../../util/save-sort-table-state/save-sort-table-hook';
import {SavedSortStateType} from '../../../../../util/save-sort-table-state/save-sort-table-type';

import {useExportCompetitors} from './competitors-page-hook';
import {ExportExcelType, PositionChangeEnum} from './competitors-page-type';
import {CompetitorsTable} from './competitors-table/competitors-table';
import {StatisticsCards} from './statistic-cards/statistic-cards';
import * as styles from './competitors-page.scss';

export function CompetitorsPage(): JSX.Element {
    const {getLocalizedString} = useLocale();

    const {snackbar} = useSnackbar();

    const {mainFilterKey} = useContext<MainFilterContextType>(MainFilterContext);

    const [positionChange, setPositionChange] = useState<PositionChangeEnum | null>(null);

    const onPositionChangeCardClick = useCallback((clickedCardPositionChange: PositionChangeEnum) => {
        setPositionChange((previousPositionChange) => {
            return previousPositionChange === clickedCardPositionChange ? null : clickedCardPositionChange;
        });
    }, []);

    const {mutate, isLoading} = useExportCompetitors({
        onSuccess: () => {
            snackbar.success({
                message: <Locale stringKey="ONLINE_PRESENCE_ANALYTICS__DOWNLOAD_EXCEL__SUCCESS__TITLE" />,
                description: <Locale stringKey="ONLINE_PRESENCE_ANALYTICS__DOWNLOAD_EXCEL__SUCCESS__MESSAGE" />,
            });
        },
        onError: () =>
            snackbar.error({
                description: <Locale stringKey="ERROR__SOMETHING_WENT_WRONG_DETAILS" />,
                message: <Locale stringKey="ERROR__SOMETHING_WENT_WRONG" />,
            }),
    });

    const [isExportModalOpen, setIsExportModalOpen] = useState(false);

    function buildExportPayload(additionalUserEmails?: string): ExportExcelType {
        return {
            // eslint-disable-next-line no-undefined
            filterKey: mainFilterKey || undefined,
            date: dayjs().format('YYYY-MM-DD'),
            comparedDate: dayjs().subtract(7, 'day').format('YYYY-MM-DD'),
            // eslint-disable-next-line no-undefined
            additionalUserEmails: additionalUserEmails || undefined,
        };
    }

    function handleExport(): void {
        setIsExportModalOpen(true);
    }

    function handleConfirmExport(additionalUserEmails?: string): void {
        setIsExportModalOpen(false);
        mutate(buildExportPayload(additionalUserEmails));
    }

    const {routerLocation, savedSortState} = useSavedTableSort();

    const [ordering, setOrdering] = useState<SavedSortStateType | null>(savedSortState);

    return (
        <Page renderContainer={(children) => <MainPageContainer>{children}</MainPageContainer>}>
            <PageCard>
                <Meta title={getLocalizedString('CATEGORY_NAME__COMPETITORS')} />
                <BreadCrumbs
                    list={[
                        {
                            path: appRoute.competitors.path,
                            titleLangKey: 'CATEGORY_NAME__COMPETITORS',
                        },
                        {
                            path: appRoute.competitorsDashboard.path,
                            titleLangKey: 'CATEGORY_NAME__COMPETITORS__DASHBOARD',
                        },
                    ]}
                />
                <PageHeader className={styles.CompetitorsPage_header}>
                    <PageHeaderTitle title="CATEGORY_NAME__COMPETITORS" />
                    <Button
                        className={styles.CompetitorsPage_downloadButton}
                        disabled={isLoading}
                        icon={<FontAwesomeIcon icon={faDownload} />}
                        onClick={handleExport}
                        target="_blank"
                    >
                        <Locale stringKey="REVIEWS_ANALYSIS__RATINGS_DYNAMICS__REPORT__DOWNLOAD_REPORT" />
                    </Button>
                </PageHeader>
                <PageCardDivider />

                <StatisticsCards
                    onPositionChangeCardClick={onPositionChangeCardClick}
                    positionChange={positionChange}
                />
            </PageCard>

            <PageCard>
                <CompetitorsTable
                    ordering={ordering}
                    positionChange={positionChange}
                    routerLocation={routerLocation}
                    setOrdering={setOrdering}
                />
            </PageCard>

            {isExportModalOpen && (
                <ExportEmailsModal
                    isLoading={isLoading}
                    isOpen={isExportModalOpen}
                    labelKey="REVIEWS_ANALYSIS__COMPETITORS__EXPORT__EMAILS_LABEL"
                    onCancel={() => setIsExportModalOpen(false)}
                    onConfirm={handleConfirmExport}
                    titleKey="REVIEWS_ANALYSIS__COMPETITORS__EXPORT__TITLE"
                />
            )}
        </Page>
    );
}
